import React from 'react';
import { Link } from 'react-router-dom';
import {
  Plane,
  Hotel,
  Anchor,
  MapPin,
  CalendarClock,
  Car,
  Users,
  CreditCard,
  Shield,
  Compass,
  Clock,
  MessageCircle,
  Phone,
  Calculator,
  ArrowRight,
  BookOpen,
  CheckCircle
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { contactInfo } from '../mock/fareData';

/*
 * 2026-09-06 — Stage 6 SEO landing page: /cruise-port-transportation
 * Targets: Nassau Cruise Port pickup/drop-off, cruise passenger transportation,
 * day-pass visitors, private Nassau tours from the cruise port, return-to-ship
 * planning. Reuses existing Taxi-98 SEO landing page visual system.
 * Informational only:
 *   - No fare changes, no booking logic changes
 *   - No new backend routes
 *   - No modifications to notifications, payment processing, or FAQ data
 * All CTAs point to existing routes: /book, /fare-calculator, /services,
 * /nassau-airport-transfers, /lpia-to-atlantis, /lpia-to-baha-mar, WhatsApp,
 * tel:, and one Hey Taxi! blog post.
 * Note: does NOT claim Nassau Cruise Port / cruise line affiliation.
 */

const highlights = [
  {
    icon: CalendarClock,
    title: 'Pre-Booked Cruise Port Pickup',
    desc: 'Reserve Taxi-98 before you arrive in Nassau. Planned pickup, clear communication and scheduled return transportation mean less uncertainty than arranging a ride after leaving the ship.'
  },
  {
    icon: Anchor,
    title: 'Cruise Port Pickup & Drop-Off',
    desc: 'Taxi-98 provides Nassau Cruise Port pickup, Nassau Cruise Port drop-off, round-trip scheduled transportation and return transportation to your ship.'
  },
  {
    icon: MapPin,
    title: 'Paradise Island & Resort Transportation',
    desc: 'Pre-book transportation from the Nassau Cruise Port to Atlantis Paradise Island, Baha Mar and other hotels and resorts across Nassau and Paradise Island.'
  },
  {
    icon: Compass,
    title: 'Private Nassau Tours',
    desc: 'Cruise passengers can pre-book a relaxing private Nassau tour with Taxi-98 — a more personal way to spend your day in port.'
  },
  {
    icon: Clock,
    title: 'Return-to-Ship Planning',
    desc: 'Schedule your return transportation to the Nassau Cruise Port in advance so you have a planned pickup time to head back to the ship.'
  },
  {
    icon: Car,
    title: 'Private Scheduled Transportation',
    desc: 'Taxi-98 provides private scheduled transportation — not shared transport. Your reservation is dedicated to your travel party for the duration of the ride.'
  }
];

const destinations = [
  'Atlantis Paradise Island',
  'RIU Palace Paradise Island',
  'Warwick Paradise Island',
  'Comfort Suites Paradise Island',
  'Baha Mar',
  'Cable Beach',
  'Margaritaville Beach Resort Nassau',
  'Downtown Nassau hotels',
  'Other Nassau and Paradise Island destinations'
];

const audience = [
  'Couples',
  'Families',
  'Small groups',
  'Solo travelers',
  'Day-pass visitors',
  'Cruise passengers taking private tours'
];

const trustPoints = [
  'Pre-booking for cruise arrivals',
  'Scheduled transportation you can plan around',
  'Clear, direct customer communication',
  'Private transportation for your travel party',
  'Nassau Cruise Port pickup and drop-off',
  'Paradise Island transportation',
  'Hotel and resort transportation',
  'Private Nassau tours',
  'Return transportation planning',
  'Professional Bahamian drivers'
];

const CruisePortTransportation = () => {
  const siteUrl = 'https://taxi-98.com';
  const canonicalPath = '/cruise-port-transportation';

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Nassau Cruise Port Transportation — Private Pickup & Drop-Off',
    serviceType: 'Cruise port transportation',
    provider: {
      '@type': 'TaxiService',
      name: 'Taxi-98',
      url: siteUrl,
      areaServed: [
        { '@type': 'Place', name: 'Nassau' },
        { '@type': 'Place', name: 'New Providence' },
        { '@type': 'Place', name: 'Paradise Island' }
      ]
    },
    areaServed: [
      { '@type': 'Place', name: 'Nassau Cruise Port' },
      { '@type': 'Place', name: 'Prince George Wharf' },
      { '@type': 'Place', name: 'Paradise Island, Bahamas' }
    ],
    url: `${siteUrl}${canonicalPath}`,
    description:
      'Pre-booked private transportation for cruise passengers arriving at the Nassau Cruise Port. Scheduled pickup and drop-off to Paradise Island, hotels, resorts and private Nassau tours. Taxi-98 is an independent transportation provider and is not affiliated with, endorsed by or officially partnered with the Nassau Cruise Port, its operators or any cruise line.'
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl + '/' },
      { '@type': 'ListItem', position: 2, name: 'Services', item: siteUrl + '/services' },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Cruise Port Transportation',
        item: siteUrl + canonicalPath
      }
    ]
  };

  return (
    <>
      <SEOHead
        title="Nassau Cruise Port Transportation | Private Taxi & Transfers | Taxi-98"
        description="Pre-book private Nassau Cruise Port transportation with Taxi-98. Scheduled pickup and drop-off for cruise passengers, transportation to Paradise Island and Nassau resorts, and private Nassau tours."
        canonical={canonicalPath}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <main
        data-testid="cruise-port-page"
        className="relative min-h-screen bg-gradient-to-br from-cyan-50 via-white to-blue-50 pt-28 pb-20 sm:pt-32 sm:pb-28"
      >
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-400/5 rounded-full blur-3xl pointer-events-none"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero */}
          <div className="max-w-3xl mx-auto text-center mb-14 sm:mb-16">
            <span
              className="inline-block bg-cyan-500/10 border border-cyan-400/30 text-cyan-700 text-xs font-bold tracking-[0.22em] uppercase px-4 py-1.5 rounded-full mb-5"
              data-testid="cruise-port-eyebrow"
            >
              Nassau Cruise Port · Paradise Island · Nassau
            </span>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-5"
              data-testid="cruise-port-headline"
            >
              Nassau Cruise Port Transportation — Private Pickup &amp; Drop-Off
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
              Taxi-98 provides pre-booked, scheduled private transportation for
              cruise passengers arriving at the Nassau Cruise Port. Reserve in
              advance and plan your day around a scheduled pickup and return.
            </p>

            <div
              className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4"
              data-testid="cruise-port-hero-ctas"
            >
              <Link
                to="/book"
                data-testid="cruise-port-cta-book"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-600 to-blue-700 hover:from-cyan-700 hover:to-blue-800 text-white px-6 py-3 rounded-lg font-semibold text-base transition-all duration-300 shadow-md"
              >
                <span>Book Cruise Port Transportation</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/fare-calculator"
                data-testid="cruise-port-cta-fare"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white border border-cyan-300 text-cyan-700 hover:bg-cyan-50 px-6 py-3 rounded-lg font-semibold text-base transition-all duration-300"
              >
                <Calculator className="w-4 h-4" />
                <span>Check Your Fare</span>
              </Link>
            </div>

            <p
              className="mt-5 text-sm text-gray-500 flex items-center justify-center gap-2 flex-wrap"
              data-testid="cruise-port-payments-compact"
            >
              <CheckCircle className="w-4 h-4 text-cyan-600" />
              <span>
                Pre-Booking Available • Major Credit Cards • Apple Pay / Tap-to-Pay • PayPal • Cash Accepted
              </span>
            </p>
          </div>

          {/* Highlights grid */}
          <section aria-labelledby="cruise-port-highlights-heading" className="mb-16">
            <h2 id="cruise-port-highlights-heading" className="sr-only">
              Nassau Cruise Port transportation highlights
            </h2>
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6"
              data-testid="cruise-port-highlights"
            >
              {highlights.map(({ icon: Icon, title, desc }) => (
                <div
                  key={title}
                  data-testid={`cruise-port-highlight-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                  className="group bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-lg hover:border-cyan-300 transition-all duration-300"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-cyan-500/10 text-cyan-600 mb-4 group-hover:bg-cyan-500/15 transition-colors">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Destinations card */}
          <section
            aria-labelledby="cruise-port-destinations-heading"
            className="mb-16 bg-white border border-gray-200 rounded-3xl p-8 sm:p-10 shadow-sm"
            data-testid="cruise-port-destinations-section"
          >
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 text-cyan-700 font-semibold uppercase text-xs tracking-widest mb-3">
                <Hotel className="w-4 h-4" />
                Cruise Port Destinations
              </div>
              <h2
                id="cruise-port-destinations-heading"
                className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4"
              >
                From the Nassau Cruise Port to your destination
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Share your destination when you book and Taxi-98 will schedule
                pickup at the Nassau Cruise Port and drop-off across Nassau and
                Paradise Island.
              </p>
            </div>
            <ul
              className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3"
              data-testid="cruise-port-destinations-list"
            >
              {destinations.map((d) => (
                <li key={d} className="flex items-start gap-2 text-gray-700">
                  <CheckCircle className="w-4 h-4 mt-1 text-cyan-600 shrink-0" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
            <p
              className="mt-6 text-xs text-gray-500 italic"
              data-testid="cruise-port-independence-disclaimer"
            >
              Taxi-98 is an independent transportation provider and is not
              affiliated with, endorsed by or officially partnered with the
              Nassau Cruise Port, its operators or any cruise line.
            </p>
          </section>

          {/* Day-Pass + Return-to-Ship */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6 mb-16">
            <section
              aria-labelledby="cruise-port-daypass-heading"
              className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-sm"
              data-testid="cruise-port-daypass-section"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-cyan-500/10 text-cyan-600 mb-4">
                <MapPin className="w-6 h-6" />
              </div>
              <h2
                id="cruise-port-daypass-heading"
                className="text-xl sm:text-2xl font-bold text-gray-900 mb-3"
              >
                Day-Pass Visitors
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Cruise passengers visiting Nassau resorts or attractions on day
                passes can pre-book Taxi-98 transportation to and from the
                property. Scheduled return transportation to the Nassau Cruise
                Port is included in the plan when you book both legs together.
              </p>
            </section>

            <section
              aria-labelledby="cruise-port-return-heading"
              className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-sm"
              data-testid="cruise-port-return-section"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-cyan-500/10 text-cyan-600 mb-4">
                <Clock className="w-6 h-6" />
              </div>
              <h2
                id="cruise-port-return-heading"
                className="text-xl sm:text-2xl font-bold text-gray-900 mb-3"
              >
                Return-to-Ship Planning
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Book your return transportation to the Nassau Cruise Port in
                advance so you have a planned pickup time to head back to the
                ship. Bahamas traffic and port conditions can vary, so we
                encourage travelers to build in comfortable time before their
                ship&#39;s all-aboard.
              </p>
            </section>
          </div>

          {/* Private Nassau Tours */}
          <section
            aria-labelledby="cruise-port-tours-heading"
            className="mb-16 bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-sm"
            data-testid="cruise-port-tours-section"
          >
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-cyan-500/10 text-cyan-600 mb-4">
              <Compass className="w-6 h-6" />
            </div>
            <h2
              id="cruise-port-tours-heading"
              className="text-xl sm:text-2xl font-bold text-gray-900 mb-3"
            >
              Private Nassau Tours
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Cruise passengers can pre-book a relaxing private Nassau tour with
              Taxi-98 — a more personal way to spend your day in port. Tour
              routes and stops depend on availability and the experience you
              choose. Reach out when you book and we will help you plan
              something that fits your day.
            </p>
          </section>

          {/* Who We Serve + Payments */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6 mb-16">
            <section
              aria-labelledby="cruise-port-audience-heading"
              className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-sm"
              data-testid="cruise-port-audience-section"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-cyan-500/10 text-cyan-600 mb-4">
                <Users className="w-6 h-6" />
              </div>
              <h2
                id="cruise-port-audience-heading"
                className="text-xl sm:text-2xl font-bold text-gray-900 mb-3"
              >
                Who We Serve
              </h2>
              <ul
                className="grid grid-cols-2 gap-x-4 gap-y-2 text-gray-700 text-sm"
                data-testid="cruise-port-audience-list"
              >
                {audience.map((a) => (
                  <li key={a} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 text-cyan-600 shrink-0" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section
              aria-labelledby="cruise-port-payments-heading"
              className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-sm"
              data-testid="cruise-port-payments-section"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-cyan-500/10 text-cyan-600 mb-4">
                <CreditCard className="w-6 h-6" />
              </div>
              <h2
                id="cruise-port-payments-heading"
                className="text-xl sm:text-2xl font-bold text-gray-900 mb-3"
              >
                Payment Options
              </h2>
              <p className="text-gray-700 mb-2 font-semibold">
                Pre-Booking Available • Major Credit Cards • Apple Pay / Tap-to-Pay • PayPal • Cash Accepted
              </p>
              <p className="text-sm text-gray-600 leading-relaxed">
                Visa, Mastercard, American Express, Discover, Apple Pay /
                Tap-to-Pay, PayPal and Cash. Payment can be completed at pickup
                or confirmed after booking.
              </p>
            </section>
          </div>

          {/* Trust positioning */}
          <section
            aria-labelledby="cruise-port-trust-heading"
            className="mb-16"
            data-testid="cruise-port-trust-section"
          >
            <div className="max-w-3xl mb-6">
              <h2
                id="cruise-port-trust-heading"
                className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3"
              >
                What cruise passengers rely on Taxi-98 for
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Every cruise-port reservation is handled the same way:
                scheduled, private and pre-planned. Here is what we focus on.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {trustPoints.map((item) => (
                <div
                  key={item}
                  className="flex items-start gap-3 bg-white border border-gray-200 rounded-xl p-4"
                >
                  <Shield className="w-5 h-5 mt-0.5 text-cyan-600 shrink-0" />
                  <p className="text-gray-700 text-sm">{item}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Internal links */}
          <section
            aria-labelledby="cruise-port-related-heading"
            className="mb-16"
            data-testid="cruise-port-related-links"
          >
            <h2
              id="cruise-port-related-heading"
              className="text-xl sm:text-2xl font-bold text-gray-900 mb-4"
            >
              Plan the rest of your Nassau ride
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link
                to="/book"
                data-testid="cruise-port-related-book"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-md rounded-xl p-5 transition-all"
              >
                <Anchor className="w-5 h-5 text-cyan-600 mb-2" />
                <p className="font-semibold text-gray-900 mb-1">Book a ride</p>
                <p className="text-sm text-gray-600">Reserve your cruise-port pickup.</p>
              </Link>
              <Link
                to="/fare-calculator"
                data-testid="cruise-port-related-fare"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-md rounded-xl p-5 transition-all"
              >
                <Calculator className="w-5 h-5 text-cyan-600 mb-2" />
                <p className="font-semibold text-gray-900 mb-1">Fare Calculator</p>
                <p className="text-sm text-gray-600">Estimate your cruise-port fare.</p>
              </Link>
              <Link
                to="/services"
                data-testid="cruise-port-related-services"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-md rounded-xl p-5 transition-all"
              >
                <Car className="w-5 h-5 text-cyan-600 mb-2" />
                <p className="font-semibold text-gray-900 mb-1">All Services</p>
                <p className="text-sm text-gray-600">Every Taxi-98 service.</p>
              </Link>
              <Link
                to="/nassau-airport-transfers"
                data-testid="cruise-port-related-airport"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-md rounded-xl p-5 transition-all"
              >
                <Plane className="w-5 h-5 text-cyan-600 mb-2" />
                <p className="font-semibold text-gray-900 mb-1">Nassau Airport Transfers</p>
                <p className="text-sm text-gray-600">LPIA arrival transportation.</p>
              </Link>
              <Link
                to="/lpia-to-atlantis"
                data-testid="cruise-port-related-atlantis"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-md rounded-xl p-5 transition-all"
              >
                <Hotel className="w-5 h-5 text-cyan-600 mb-2" />
                <p className="font-semibold text-gray-900 mb-1">LPIA to Atlantis</p>
                <p className="text-sm text-gray-600">Paradise Island transfers.</p>
              </Link>
              <Link
                to="/lpia-to-baha-mar"
                data-testid="cruise-port-related-bahamar"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-md rounded-xl p-5 transition-all"
              >
                <Hotel className="w-5 h-5 text-cyan-600 mb-2" />
                <p className="font-semibold text-gray-900 mb-1">LPIA to Baha Mar</p>
                <p className="text-sm text-gray-600">Cable Beach resort transfers.</p>
              </Link>
              <Link
                to="/hey-taxi/resort-visitors-cruise-visitors-island-explorers-bahamas-trip"
                data-testid="cruise-port-related-blog"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-md rounded-xl p-5 transition-all"
              >
                <BookOpen className="w-5 h-5 text-cyan-600 mb-2" />
                <p className="font-semibold text-gray-900 mb-1">Cruise Visitor Guide</p>
                <p className="text-sm text-gray-600">A Hey Taxi! read for cruise travelers.</p>
              </Link>
              <Link
                to="/#faq"
                data-testid="cruise-port-related-faq"
                className="group bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-md rounded-xl p-5 transition-all"
              >
                <MessageCircle className="w-5 h-5 text-cyan-600 mb-2" />
                <p className="font-semibold text-gray-900 mb-1">FAQs</p>
                <p className="text-sm text-gray-600">Common cruise passenger questions.</p>
              </Link>
            </div>
          </section>

          {/* CTA panel */}
          <div
            className="bg-gradient-to-br from-cyan-600 to-blue-700 rounded-3xl p-8 sm:p-10 lg:p-12 text-white shadow-xl"
            data-testid="cruise-port-cta-panel"
          >
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3">
                Ready to reserve your Nassau Cruise Port ride?
              </h2>
              <p className="text-base sm:text-lg text-cyan-50 mb-8">
                Pre-book online, estimate your fare in seconds, or reach a real
                person on WhatsApp or by phone. We are happy to help with
                anything special you need.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                <Link
                  to="/book"
                  data-testid="cruise-port-cta-book-panel"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-cyan-700 hover:bg-cyan-50 px-6 py-3 rounded-lg font-semibold text-base transition-all duration-300 shadow-md"
                >
                  <span>Book Cruise Port Transportation</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>

                <Link
                  to="/fare-calculator"
                  data-testid="cruise-port-cta-fare-panel"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-cyan-700/40 border border-white/30 hover:bg-cyan-700/60 text-white px-6 py-3 rounded-lg font-semibold text-base transition-all duration-300 backdrop-blur-sm"
                >
                  <Calculator className="w-4 h-4" />
                  <span>Check Your Fare</span>
                </Link>

                <a
                  href={`https://wa.me/${contactInfo.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(
                    'Hello Taxi-98, I would like to pre-book Nassau Cruise Port transportation.'
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="cruise-port-cta-whatsapp"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-semibold text-base transition-all duration-300 shadow-md"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>WhatsApp Us</span>
                </a>
              </div>

              <div className="mt-7 pt-7 border-t border-white/20 text-sm sm:text-base text-cyan-50">
                <p className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3">
                  <span className="inline-flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <a
                      href={`tel:${contactInfo.phone.replace(/[^+\d]/g, '')}`}
                      data-testid="cruise-port-cta-phone"
                      className="font-semibold hover:underline"
                    >
                      {contactInfo.phone}
                    </a>
                  </span>
                  <span className="hidden sm:inline opacity-50">•</span>
                  <span className="opacity-90">{contactInfo.hours}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default CruisePortTransportation;
